import type {
  AdminIdentity,
  AuditEvent,
  Customer,
  Product,
  ProductKeyMetadata,
  ProductUsageSnapshot,
  SearchResult,
} from "./types";

const now = "2026-08-12T09:14:00Z";

export const demoProductUsage: ProductUsageSnapshot = {
  generated_at: now,
  window_started_at: "2026-08-05T09:14:00Z",
  window: "7d",
  segment: "paid",
  products: [
    { id: "prod_desktop", key: "desktop-pro", name: "Desktop Pro", status: "active", effective_active_licenses: 18, lifecycle_active_activations: 27, recently_seen_devices: 21, latest_successful_contact: "2026-08-12T08:52:00Z", new_activations: 4, deactivated_activations: 1, device_capacity: { available: true, current: "27", limit: "72", utilization_percent: 37.5 }, metric_availability: { effective_active_licenses: true, device_capacity: true } },
    { id: "prod_api", key: "api-runtime", name: "API Runtime", status: "active", effective_active_licenses: 4, lifecycle_active_activations: 6, recently_seen_devices: 3, latest_successful_contact: "2026-08-11T21:05:00Z", new_activations: 1, deactivated_activations: 0, device_capacity: { available: true, current: "6", limit: "16", utilization_percent: 37.5 }, metric_availability: { effective_active_licenses: true, device_capacity: true } },
  ],
};

export const demoResults: SearchResult[] = [
  {
    product: {
      id: "prod_desktop",
      key: "desktop-pro",
      name: "Desktop Pro",
      platforms: ["windows", "darwin"],
      feature_catalog: { "feature.export": "boolean", "feature.sync": "boolean", "feature.api": "boolean" },
      status: "active",
      created_at: "2026-07-01T08:00:00Z",
    },
    customer: { id: "cust_acme", name: "Acme Co.", status: "active", created_at: "2026-07-01T08:00:00Z" },
    license: {
      id: "lic_acme_desktop",
      customer_id: "cust_acme",
      product_id: "prod_desktop",
      kind: "paid",
      model: "node_locked",
      status: "active",
      valid_from: "2026-08-01T00:00:00Z",
      valid_until: "2026-12-31T23:59:59Z",
      entitlements: {
        "feature.export": { key: "feature.export", type: "boolean", value: true },
        "feature.sync": { key: "feature.sync", type: "boolean", value: true },
        "feature.api": { key: "feature.api", type: "boolean", value: true },
      },
      policy: {
        version: 1,
        max_devices: 5,
        max_token_lifetime_seconds: 2592000,
        refresh_after_seconds: 86400,
        warning_before_expiry_seconds: 604800,
        offline_grace_seconds: 2592000,
        clock_rollback_seconds: 300,
        clock_check_window_seconds: 86400,
        post_expiration: "restricted",
      },
      created_at: "2026-08-01T08:00:00Z",
      updated_at: now,
    },
    activation_count: 2,
    active_activation_count: 2,
    activations: [
      {
        id: "act_install_01",
        license_id: "lic_acme_desktop",
        product_id: "prod_desktop",
        installation_id: "install-01",
        computer_name: "install-01",
        platform: "windows",
        app_version: "1.8.2",
        app_version_reported_at: "2026-08-12T08:52:00Z",
        status: "active",
        created_at: "2026-08-02T07:52:00Z",
        last_seen_at: "2026-08-12T08:52:00Z",
      },
      {
        id: "act_install_02",
        license_id: "lic_acme_desktop",
        product_id: "prod_desktop",
        installation_id: "install-02",
        computer_name: "install-02",
        platform: "darwin",
        app_version: "1.8.1",
        app_version_reported_at: "2026-08-12T07:14:00Z",
        status: "active",
        created_at: "2026-08-04T10:30:00Z",
        last_seen_at: "2026-08-12T07:14:00Z",
      },
    ],
    activations_truncated: false,
    key_suffixes: ["····7B42"],
    key_suffixes_truncated: false,
  },
  {
    product: {
      id: "prod_api",
      key: "api-runtime",
      name: "API Runtime",
      platforms: ["windows", "darwin"],
      feature_catalog: { "feature.api": "boolean" },
      status: "active",
      created_at: "2026-06-01T08:00:00Z",
    },
    customer: { id: "cust_northstar", name: "Northstar", status: "active", created_at: "2026-06-01T08:00:00Z" },
    license: {
      id: "lic_northstar_api",
      customer_id: "cust_northstar",
      product_id: "prod_api",
      kind: "trial",
      model: "time_limited",
      status: "suspended",
      valid_from: "2026-06-01T00:00:00Z",
      valid_until: "2026-09-30T23:59:59Z",
      entitlements: { "feature.api": { key: "feature.api", type: "boolean", value: true } },
      policy: {
        version: 1,
        max_devices: 2,
        max_token_lifetime_seconds: 2592000,
        refresh_after_seconds: 86400,
        warning_before_expiry_seconds: 604800,
        offline_grace_seconds: 2592000,
        clock_rollback_seconds: 300,
        clock_check_window_seconds: 86400,
        post_expiration: "restricted",
      },
      created_at: "2026-06-01T08:00:00Z",
      updated_at: "2026-08-11T14:22:00Z",
    },
    activation_count: 0,
    active_activation_count: 0,
    activations: [],
    activations_truncated: false,
    key_suffixes: ["····A19C"],
    key_suffixes_truncated: false,
  },
];

export const demoTimelines: Record<string, AuditEvent[]> = {
  lic_acme_desktop: [
    {
      id: "audit_901",
      actor_id: "narin",
      actor_role: "License Manager",
      action: "license.updated",
      target_type: "license",
      target_id: "lic_acme_desktop",
      reason: "Seat increase",
      correlation_id: "corr_8f21",
      outcome: "success",
      after: { "policy.max_devices": 5 },
      created_at: now,
    },
    {
      id: "audit_900",
      actor_id: "product",
      actor_role: "product",
      action: "activation.refreshed",
      target_type: "activation",
      target_id: "act_install_01",
      reason: "Scheduled refresh",
      correlation_id: "corr_8f20",
      outcome: "success",
      created_at: "2026-08-12T08:52:00Z",
    },
    {
      id: "audit_899",
      actor_id: "mali",
      actor_role: "License Manager",
      action: "license.created",
      target_type: "license",
      target_id: "lic_acme_desktop",
      reason: "Pilot seed",
      correlation_id: "corr_7aa2",
      outcome: "success",
      created_at: "2026-08-01T08:00:00Z",
    },
  ],
  lic_northstar_api: [
      {
        id: "audit_712",
        actor_id: "security-1",
        actor_role: "Security Admin",
        action: "license.updated",
        target_type: "license",
        target_id: "lic_northstar_api",
        reason: "Customer billing hold",
        correlation_id: "corr_2d10",
        outcome: "success",
        created_at: "2026-08-11T14:22:00Z",
      },
  ],
};

export const demoIdentity: AdminIdentity = {
  actor_id: "local-demo",
  username: "development-admin",
  roles: ["Viewer"],
};

export const demoProducts: Product[] = demoResults.map(({ product }) => product);
export const demoCustomers: Customer[] = demoResults.map(({ customer }) => customer);

export const demoProductKeys: ProductKeyMetadata[] = [
  {
    id: "key_demo_acme_01",
    license_id: "lic_acme_desktop",
    product_id: "prod_desktop",
    masked_prefix: "ABCDE",
    masked_suffix: "X7B42",
    status: "active",
    batch_id: "batch_demo_acme",
    created_at: "2026-08-12T08:52:00Z",
    revoked_at: null,
  },
  {
    id: "key_demo_northstar_01",
    license_id: "lic_northstar_api",
    product_id: "prod_api",
    masked_prefix: "FGHJK",
    masked_suffix: "ZA19C",
    status: "revoked",
    batch_id: "batch_demo_northstar",
    created_at: "2026-08-11T14:22:00Z",
    revoked_at: "2026-08-12T07:14:00Z",
  },
];

export function demoSearch(query: string) {
  const needle = query.trim().toLowerCase();
  if (!needle) return { results: demoResults };
  return {
    results: demoResults.filter((item) => {
      const values = [
        item.license.id,
        item.customer.id,
        item.customer.name,
        item.product.id,
        item.product.name,
        ...item.key_suffixes,
        ...item.activations.flatMap((activation) => [activation.id, activation.installation_id, activation.platform]),
      ];
      return values.some((value) => value.toLowerCase().includes(needle));
    }),
  };
}
